import { NOTIFICATION_POLL_MS } from '@hm/contracts'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Bell } from 'lucide-react'
import { api } from '~/lib/api'
import { useT } from '~/lib/i18n'
import { useSession } from '~/lib/session'
import { anchor } from '~/lib/tour/anchors'

/**
 * The unread count, polled on a slow loop.
 *
 * Sixty seconds is deliberately unhurried: anything genuinely urgent arrives as a
 * push notification, and a bell that refreshes every few seconds on every screen
 * would be the app's largest source of requests for the smallest benefit.
 */
export function NotificationBell() {
  const t = useT()
  const user = useSession()
  const enabled = user.permissions.includes('notification:read')

  const count = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => api.get<{ unread: number }>('/api/notifications/count'),
    enabled,
    refetchInterval: NOTIFICATION_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: NOTIFICATION_POLL_MS,
  })

  if (!enabled) return null
  const unread = count.data?.unread ?? 0

  return (
    <Link
      {...anchor('shell.bell')}
      to="/notifications"
      className="relative grid size-10 shrink-0 place-items-center rounded-control text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
      aria-label={
        unread > 0
          ? t('notification_bell.notifications_unread_unread', { unread: unread })
          : t('common.notifications')
      }
    >
      <Bell className="size-5" />
      {unread > 0 ? (
        <span className="absolute top-1 right-1 grid min-w-4 place-items-center rounded-full bg-primary px-1 text-[0.625rem] font-extrabold text-on-primary tnum">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </Link>
  )
}
